import axios from 'axios';
import config from '../../config';
import { apiCode, apiConfig } from '../../services';
import { loadUserStats } from '../user';
import { clearImage, setSaving, setShowSuccess } from '../ui';
import { SystemText } from '@salesperformanceint/e4shared';

export const ADMIN_LOAD_SUCCESS = 'ADMIN_LOAD_SUCCESS';
export const ADMIN_LOADING = 'ADMIN_LOADING';
export const ADMIN_LOADING_ERROR = 'ADMIN_LOADING_ERROR';
export const ADMIN_CLEAR_ERROR = 'ADMIN_CLEAR_ERROR';

export const ERROR_TYPES = Object.freeze({ General: 'generalErrors', NotUnique: 'uniqueErrors' });

export const serverError = "We're very sorry, but something has broken on the server.";
export const uniqueError = 'UNIQUE_ERROR';

export const adminLoading = payload => {
    return {
        type: ADMIN_LOADING,
        payload,
    };
};

export const adminError = payload => {
    return {
        type: ADMIN_LOADING_ERROR,
        payload,
    };
};

export const clearError = () => {
    return {
        type: ADMIN_CLEAR_ERROR,
    };
};

export const loadSuccess = payload => {
    return {
        type: ADMIN_LOAD_SUCCESS,
        payload,
    };
};

export const load = (route, background) => {
    return (dispatch, getState) => {
        !background && dispatch(adminLoading(true));
        !background && dispatch(clearError());

        const { guid } = getState().user.user;

        return axios
            .get(`${config.apiGateway.URL}${route}${apiCode()}`, apiConfig(guid))
            .then(response => {
                dispatch(loadSuccess({ route, data: response.data }));
                !background && dispatch(adminLoading(false));
            })
            .catch(err => {
                dispatch(
                    adminError({ errorMessage: err.response ? err.response.data : null, errorType: ERROR_TYPES.General })
                );
                !background && dispatch(adminLoading(false));
            });
    };
};

// The users the signed-in support user may spoof. auth/spoof/users is authorised against the person
// really signed in, so it still works while spoofing a user who isn't an admin, unlike GET profile.
// It needs the refresh cookie, which a cross-origin API only gets with credentials.
export const loadSpoofCandidates = () => {
    return (dispatch, getState) => {
        dispatch(adminLoading(true));
        dispatch(clearError());

        const { guid } = getState().user.user;

        return axios
            .get(`${config.apiGateway.URL}auth/spoof/users${apiCode()}`, { ...apiConfig(guid), withCredentials: true })
            .then(response => {
                dispatch(loadSuccess({ route: 'auth/spoof/users', data: response.data }));
            })
            .catch(err => {
                dispatch(
                    adminError({ errorMessage: err.response ? err.response.data : null, errorType: ERROR_TYPES.General })
                );
            })
            .finally(() => {
                dispatch(adminLoading(false));
            });
    };
};

export const add = (route, entity, files, callback, entityOverride) => {
    return (dispatch, getState) => {
        dispatch(adminLoading(true));
        dispatch(clearError());
        const { guid } = getState().user.user;
        const promises = filePromises(entity, files, guid, config);
        return Promise.all(promises).then(() =>
            axios
                .post(`${config.apiGateway.URL}${route}${apiCode()}`, entity, apiConfig(guid))
                .then(response => {
                    handleSuccess(dispatch, entityOverride || route);
                    if (route === 'task') {
                        dispatch(loadUserStats());
                    }

                    callback?.(response.data);
                })
                .catch(err => {
                    handleError(dispatch, err);
                    dispatch(setSaving(false));
                })
        );
    };
};

export const update = (route, entity, files, callback, fireHandleSuccess = true, entityOverride) => {
    return (dispatch, getState) => {
        dispatch(adminLoading(true));
        dispatch(clearError());

        const { guid } = getState().user.user;

        return Promise.all(filePromises(entity, files, guid, config)).then(() =>
            axios
                .patch(`${config.apiGateway.URL}${route}${apiCode()}`, entity, apiConfig(guid))
                .then(() => {
                    files?.forEach(f => {
                        if (f.toLowerCase().indexOf('_image') > 0 || f === 'avatar') {
                            if (entity[`${f}Meta`]) {
                                dispatch(clearImage(f, entity[f]));
                            }
                        }
                    });

                    fireHandleSuccess && handleSuccess(dispatch, entityOverride || route);
                    if (route === 'task') {
                        dispatch(loadUserStats());
                    }
                    callback?.();
                })
                .catch(err => {
                    handleError(dispatch, err);
                    dispatch(setSaving(false));
                })
        );
    };
};

export const filePromises = (entity, files, guid, config) => {
    const filePromises = [];
    const fileConfig = apiConfig(guid, true);
    files?.forEach(f => {
        if (f) {
            if (entity[f] && !entity[f].id && (entity[f].name || entity[`${f}_fileName`])) {
                filePromises.push(uploadFile(entity, f, config, fileConfig));
            }
        }
    });

    return filePromises;
};

export const benchmark = callback => {
    return (dispatch, getState) => {
        const { guid } = getState().user.user;
        return axios
            .patch(`${config.apiGateway.URL}benchmark${apiCode()}`, null, apiConfig(guid))
            .then(response => {
                callback?.();
            })
            .catch(err => {
                // console.log(err);
            });
    };
};

export const remove = (route, id, callback) => {
    return (dispatch, getState) => {
        dispatch(adminLoading(true));
        dispatch(clearError());

        const { guid } = getState().user.user;

        return axios
            .delete(`${config.apiGateway.URL}${route}/${id}${apiCode()}`, apiConfig(guid))
            .then(() => {
                dispatch(adminLoading(false));
                callback?.();
            })
            .catch(err => {
                handleError(dispatch, err);
            });
    };
};

export const uploadFile = (entity, f, config, headerConfig, callback) => {
    let bodyFormData = new FormData();
    if (entity[`${f}_fileName`]) {
        bodyFormData.append('video', entity[f], entity[`${f}_fileName`]);
    } else {
        bodyFormData.append('image', entity[f]);
    }

    const companyId = entity[f].companyId ? entity[f].companyId : 0;
    const partnerId = entity[f].partnerId ? entity[f].partnerId : 0;

    return axios
        .post(`${config.apiGateway.URL}upload/${f}/${companyId}/${partnerId}${apiCode()}`, bodyFormData, headerConfig)
        .then(response => {
            const file = response.data.path;
            entity[f] = file;
            callback?.(response);
        })
        .catch(() => {
            entity[f] = '';
        });
};

const getErrorType = code => {
    switch (code) {
        case 409:
            return ERROR_TYPES.NotUnique;
        default:
            return ERROR_TYPES.General;
    }
};

export const handleError = (dispatch, error) => {
    const errorType = getErrorType(error.response?.status);
    const errorMessage =
        errorType === ERROR_TYPES.General
            ? serverError
            : errorType === ERROR_TYPES.uniqueError
              ? uniqueError
              : error.response.data;
    dispatch(adminError({ errorMessage, errorType }));
    dispatch(adminLoading(false));
};

export const handleSuccess = (dispatch, route) => {
    dispatch(setShowSuccess(SystemText('Admin/General/Saved', route.replace('-', ' ').toLowerCase())));
    dispatch(adminLoading(false));
};
